/**
 * Wat er te zien is van de beurt van de ander.
 *
 * De server stuurt sinds kort de zetten mee. Dit bestand beslist wélke daarvan een animatie
 * krijgen en in welke volgorde — puur rekenwerk, zonder pixi en zonder klok, zodat het in
 * vitest past. Het afspelen zelf staat in `main.ts`; dit is alleen het draaiboek.
 *
 * Waarom niet gewoon elke zet naspelen: dat kan niet, en het zou ook niet moeten.
 *
 * **Het kan niet** omdat de weergave maar één worp draagt. `view.dice` en
 * `view.lastEventCard` gaan over de láátste worp; kwamen er twee binnen in één poll, dan
 * bestaat de eerste nergens meer in de gegevens die de client heeft. Twee dobbelanimaties
 * spelen zou betekenen dat we er één verzinnen.
 *
 * **Het zou niet moeten** omdat een volle worp elf seconden duurt. Speelt de ander vlot
 * door, dan groeit de wachtrij sneller dan hij leegloopt en begint jouw beurt onder de
 * banner van de vorige. Vandaar het tijdbudget: boven de grens houden we de worp, de laatste
 * kaart en de opbrengst, en vervalt de rest.
 *
 * Klank hoort bij zijn stap. Het draaiboek zegt daarom per zet of een stap hem `dekt`:
 * gedekte zetten klinken op het moment dat hun stap in beeld komt, en alléén zetten die
 * geen stap kregen (gesnoeid door het budget of de flitsgrens) klinken vooraf — als enig
 * spoor van wat er visueel wegviel. Vroeger klonk álles vooraf, en dan hoorde je de
 * bouwklanken al terwijl de steen nog rolde.
 */
import type { Seat } from '@catan/contracts';
import { aanvalsworpUit, gooiUit } from '@catan/rules';

/** De zijde van een bouwwerk waar een uitbouw ligt. */
export type Zijde = 'boven' | 'onder';

/**
 * Een zet zoals de server hem stuurt.
 *
 * `actie` is bewust smal getypt: de server saneert hem met `zichtbareActie` tot hoogstens
 * zes velden, en meer dan dit heeft het scherm nooit nodig gehad.
 */
export interface Zichtbarezet {
  readonly seq: number;
  readonly seat: Seat;
  readonly actie: {
    readonly type: string;
    readonly column?: number;
    readonly side?: Zijde;
    readonly cardId?: number;
    readonly accepteren?: boolean;
    readonly inzetten?: boolean;
  };
  readonly log: readonly string[];
}

export type Afspeelstap =
  | {
      readonly soort: 'bouw';
      readonly seat: Seat;
      readonly column: number;
      readonly side: Zijde;
      /** Alleen bij een uitbouw: het kaartnummer, zodat de vloot zijn eigen klank krijgt. */
      readonly cardId?: number;
    }
  | { readonly soort: 'kaart'; readonly cardId: number }
  | {
      readonly soort: 'worp';
      /**
       * De waarden uit het verslag van de gooi-zet zelf. De levende weergave is ze al
       * kwijt zodra de beurt in dezelfde partij ook eindigde — en dan sloeg het scherm
       * de steen stilzwijgend over terwijl de bouwflitsen al draaiden.
       */
      readonly production?: 1 | 2 | 3 | 4 | 5 | 6;
      readonly event?: string;
    }
  /** De worp van een aanvalskaart: één dobbelsteen, met de uitslag uit het verslag. */
  | { readonly soort: 'aanval'; readonly regels: readonly string[] }
  /** Wat de Spion uit je hand nam — het slachtoffer ziet de kaart groot in beeld. */
  | { readonly soort: 'gestolen'; readonly naam: string }
  | { readonly soort: 'ruil'; readonly aangenomen: boolean }
  /**
   * Het kaartwerk aan het eind van een beurt: een handkaart onder een stapel ruilen of
   * de hand aanvullen. Zonder deze melding leek de beurt van de ander abrupt te eindigen
   * — je hoorde de kaartklank maar zag niet wat er gebeurde.
   */
  | { readonly soort: 'kaartwissel'; readonly seat: Seat; readonly wat: 'ruil' | 'aanvul' }
  | { readonly soort: 'opbrengst' };

/**
 * Wat elke stap ongeveer kost, in milliseconden.
 *
 * Geen exacte tijden — die hangen af van de stille modus — maar de verhouding klopt, en
 * daar gaat het bij het budget om. De worp is met afstand de duurste: dobbelanimatie plus
 * kaart plus banner plus rust.
 */
export const KOSTEN: Readonly<Record<Afspeelstap['soort'], number>> = {
  kaartwissel: 1_600,
  worp: 11_000,
  kaart: 4_700,
  // Verweer-mededeling + rollende steen + uitslagbanner met de kaart erin.
  aanval: 8_900,
  gestolen: 4_700,
  bouw: 1_400,
  ruil: 1_800,
  opbrengst: 1_200,
};

/** Boven deze grens condenseert het plan tot worp + laatste kaart + opbrengst. */
export const BUDGET_MS = 12_000;

/** Hoogstens zoveel bouwflitsen achter elkaar; daarboven wordt het een diavoorstelling. */
export const MAX_BOUWFLITSEN = 4;

const BOUWT = new Set(['bouw-straat', 'bouw-dorp', 'bouw-stad', 'bouw-uitbreiding']);

/** Zetten die een kaart groot in beeld verdienen. */
function kaartVan(zet: Zichtbarezet): number | undefined {
  const a = zet.actie;
  if (a.cardId === undefined) return undefined;
  if (a.type === 'speel-actiekaart' || a.type === 'bouw-uitbreiding') return a.cardId;
  if (a.type === 'tegenkaart' && a.inzetten === true) return a.cardId;
  return undefined;
}

/**
 * De kaart die de Spion uit een hand nam, als die zet erbij zat.
 *
 * De vorm komt uit `turn.ts`; die regel noemt de kaart bij naam omdat beide spelers hem
 * toch al kennen — de dief zag de hand, het slachtoffer kent zijn eigen kaarten. Hier lezen
 * we hem terug zodat de bestolene ziet wát er weg is in plaats van alleen zijn hand te zien
 * krimpen.
 */
function gestolenKaart(regels: readonly string[]): string | undefined {
  for (const regel of regels) {
    const treffer = / steelt de (.+) uit de hand van /.exec(regel);
    if (treffer !== null) return treffer[1];
  }
  return undefined;
}

export interface Afspeelopties {
  /**
   * Is er een gat tussen wat we hebben en wat er gebeurd is?
   *
   * Dat kan door een momentopname (de server bewaart maar twintig zetten terug) of een
   * gemiste poll. Dan is het plan leeg: een schone sprong is eerlijker dan een halve
   * reconstructie die suggereert dat je alles gezien hebt.
   */
  readonly gat: boolean;
  /** Ruimte voor animatie. Standaard `BUDGET_MS`; los instelbaar maakt het toetsbaar. */
  readonly budget?: number;
}

/**
 * Het draaiboek: de stappen én welke zetten daarmee gedekt zijn.
 *
 * `gedekt` bevat de seq's van zetten waarvan een stap het overnam. Hun klank speelt op het
 * vertoonmoment van die stap; alle andere zetten klinken vooraf. De koppeling wordt pas ná
 * het snoeien gelegd, zodat een gesneuvelde stap zijn zetten netjes aan het klank-spoor
 * teruggeeft.
 */
export interface Draaiboek {
  readonly stappen: readonly Afspeelstap[];
  readonly gedekt: ReadonlySet<number>;
}

/** Een stap met de seq's van de zetten die hij voor zijn rekening neemt. */
interface Gepland {
  readonly stap: Afspeelstap;
  readonly dekt: readonly number[];
}

/**
 * Het draaiboek voor een reeks zetten van de ander.
 *
 * Volgorde binnen het plan is de volgorde waarin de zetten binnenkwamen; alleen de worp
 * springt naar zijn eigen plek, want die hoort bij de stand die het scherm nú heeft.
 */
export function afspeelplan(
  zetten: readonly Zichtbarezet[],
  opties: Afspeelopties,
): Draaiboek {
  if (opties.gat || zetten.length === 0) return { stappen: [], gedekt: new Set() };

  const gepland: Gepland[] = [];
  let bouwflitsen = 0;

  // Hoogstens één worp en één kaart, en dan de láátste: dat is de enige waarvan de gegevens
  // nog in de weergave staan. De worp-stap dekt wel álle gooi-zetten — een eerdere worp uit
  // dezelfde partij bestaat nergens meer, dus vooraf klinken zou een steen suggereren die
  // nooit in beeld komt.
  const laatsteWorp = zetten.map((z) => z.actie.type).lastIndexOf('gooi');
  const gooiSeqs = zetten.filter((z) => z.actie.type === 'gooi').map((z) => z.seq);
  let laatsteKaart = -1;
  for (let i = 0; i < zetten.length; i++) if (kaartVan(zetten[i]!) !== undefined) laatsteKaart = i;

  for (let i = 0; i < zetten.length; i++) {
    const zet = zetten[i]!;
    const a = zet.actie;

    if (i === laatsteWorp) {
      const worp = gooiUit(zet.log);
      gepland.push({ stap: { soort: 'worp', ...(worp ?? {}) }, dekt: gooiSeqs });
      continue;
    }
    if (i === laatsteKaart) {
      const cardId = kaartVan(zet);
      if (cardId !== undefined) gepland.push({ stap: { soort: 'kaart', cardId }, dekt: [zet.seq] });
      continue;
    }
    if (
      BOUWT.has(a.type) &&
      a.column !== undefined &&
      bouwflitsen < MAX_BOUWFLITSEN
    ) {
      bouwflitsen++;
      gepland.push({
        stap: {
          soort: 'bouw',
          seat: zet.seat,
          column: a.column,
          side: a.side ?? 'onder',
          ...(a.type === 'bouw-uitbreiding' && a.cardId !== undefined ? { cardId: a.cardId } : {}),
        },
        dekt: [zet.seq],
      });
      continue;
    }
    if (a.type === 'ruil-antwoord' && a.accepteren !== undefined) {
      gepland.push({ stap: { soort: 'ruil', aangenomen: a.accepteren }, dekt: [zet.seq] });
    }
    // Eén melding per soort kaartwerk per rondje — drie keer "vult zijn hand aan" is
    // drammen, nul keer is een beurt die uit het niets eindigt. De ene stap dekt ze allemaal:
    // drie losse kaartklanken vooraf zouden hetzelfde drammen zijn, maar dan hoorbaar.
    if (a.type === 'ruil-kaart' || a.type === 'ruil-af') {
      dekOfBegin(gepland, zet, 'ruil');
    }
    if (a.type === 'pak-kaart') {
      dekOfBegin(gepland, zet, 'aanvul');
    }
  }

  // De aanvalsworp en de diefstal van de Spion lezen we uit het verslag van de zetten zelf.
  // Ze staan los van de lus hierboven omdat ze niet aan één actietype hangen: een aanval
  // kan uit `speel-actiekaart` komen of uit het `tegenkaart`-antwoord erna, en de Spion pas
  // uit de `kies` waarmee de dief zijn kaart aanwijst. Ze dekken niets: hun zet is meestal
  // al gedekt door de kaart-stap, en hun eigen klanken zitten in de stap zelf.
  for (const zet of zetten) {
    if (aanvalsworpUit(zet.log) !== undefined) {
      gepland.push({ stap: { soort: 'aanval', regels: zet.log }, dekt: [] });
      break;
    }
  }
  for (const zet of zetten) {
    const naam = gestolenKaart(zet.log);
    if (naam !== undefined) {
      gepland.push({ stap: { soort: 'gestolen', naam }, dekt: [] });
      break;
    }
  }

  // De opbrengst hoort er één keer achteraan — maar alleen als er geen worp in het plan zit:
  // `speelWorpUit` eindigt zelf al met `laatOpbrengstAankomen`, en twee keer laten aankomen
  // toont dezelfde grondstoffen twee keer.
  const heeftWorp = gepland.some((g) => g.stap.soort === 'worp');
  if (!heeftWorp && gepland.length > 0) gepland.push({ stap: { soort: 'opbrengst' }, dekt: [] });

  const overleefd = binnenBudget(gepland, opties.budget ?? BUDGET_MS);
  return {
    stappen: overleefd.map((g) => g.stap),
    gedekt: new Set(overleefd.flatMap((g) => g.dekt)),
  };
}

/** Voegt de zet toe aan de bestaande kaartwissel-stap van dit soort, of begint hem. */
function dekOfBegin(gepland: Gepland[], zet: Zichtbarezet, wat: 'ruil' | 'aanvul'): void {
  const plek = gepland.findIndex((g) => g.stap.soort === 'kaartwissel' && g.stap.wat === wat);
  if (plek >= 0) {
    gepland[plek] = { stap: gepland[plek]!.stap, dekt: [...gepland[plek]!.dekt, zet.seq] };
    return;
  }
  gepland.push({ stap: { soort: 'kaartwissel', seat: zet.seat, wat }, dekt: [zet.seq] });
}

/**
 * Snoeit het plan tot het binnen de tijd past.
 *
 * Wat overblijft is wat je niet kunt missen: de worp (die de stand verklaart), de laatste
 * kaart (die vertelt wát er gebeurde) en de opbrengst (die het verschil laat landen).
 * Bouwflitsen en ruilanimaties zijn het eerste dat sneuvelt — daarvan zie je het resultaat
 * toch op het bord, en hun klank speelt dan weer vooraf.
 */
function binnenBudget(gepland: Gepland[], budget: number): Gepland[] {
  const totaal = (lijst: readonly Gepland[]): number =>
    lijst.reduce((som, g) => som + KOSTEN[g.stap.soort], 0);
  if (totaal(gepland) <= budget) return gepland;

  const kern = gepland.filter(
    (g) =>
      g.stap.soort === 'worp' ||
      g.stap.soort === 'kaart' ||
      g.stap.soort === 'aanval' ||
      g.stap.soort === 'gestolen' ||
      g.stap.soort === 'opbrengst',
  );
  // Ook de kern kan te duur zijn (worp + kaart is al bijna zestien seconden). Dan wint de
  // worp: die draagt de gebeurtenis, de kaart en de banner al in zich.
  if (totaal(kern) > budget && kern.some((g) => g.stap.soort === 'worp')) {
    return kern.filter((g) => g.stap.soort !== 'kaart');
  }
  return kern;
}
